import {createSlice, ActionReducerMapBuilder, current} from '@reduxjs/toolkit'
import {
  AssignedPolicyTypes,
  PoliciesItemsTypes,
  PoliciesLastEvaluatedKeyTypes,
  Policy,
  SliceStatus,
  UnknownObject,
  SSLInspectionStatus,
} from 'interfaces'
import {
  createPolicy,
  createPolicyFailure,
  createPolicySuccess,
  updatePolicyUpdateField,
  resetPolicyUpdateField,
  formSubmitted,
  savePressed,
  setShowCreatePolicyRP,
  deletePolicy,
  deletePolicyFailure,
  deletePolicySuccess,
  resetPolicyList,
  updatePolicyName,
  setSSLInspectionData,
  setSSLInspectionSnapshot,
  setSSLInspectionBasePolicy,
} from './policiesActions'

import {
  getPolicyAssignee,
  getPolicyAssigneeFailure,
  getPolicyAssigneeSuccess,
  assignPolicy,
  assignPolicyFailure,
  assignPolicySuccess,
  changeAssignedPolicy,
  removeAssignedPolicySnapshot,
  setAssignedUsersGroupsToDelete,
  getPolicyClashGroups,
  getPolicyClashGroupsFailure,
  getPolicyClashGroupsSuccess,
  resetPolicyClash,
  resetPolicyUserList,
} from './Users/usersActions'

import {
  getCloudApp,
  getCloudAppSuccess,
  getCloudAppFailure,
  updateCloudApp,
  updateCloudAppSuccess,
  updateCloudAppFailure,
  getBasePolicyCloudApp,
  getBasePolicyCloudAppSuccess,
  getBasePolicyCloudAppFailure,
  changeCloudApp,
  removeCloudAppSnapshot,
} from './CloudApp/cloudAppActions'

import {isEqual} from 'lodash'
import {
  CloudAppResponseType,
  PolicyClashDataInterface,
  PolicyUpdateField,
} from 'interfaces/Policy'
import {isEmptyObject} from 'utils'
import {box, defaultCloudApplication, salesforce} from './policyHelpers'

type PoliciesSliceState = {
  sslInspectionState: {
    data: SSLInspectionStatus
    snapshot: SSLInspectionStatus
    basePolicy: SSLInspectionStatus
  }
  policies: {
    items: PoliciesItemsTypes[]
    lastEvaluateKey: PoliciesLastEvaluatedKeyTypes
    hasMore: boolean
    status: SliceStatus
    itemCount: number
  }

  showCreatePolicyRP: boolean
  createPolicyStatus: SliceStatus
  updatePolicyStatus: SliceStatus
  deletePolicyStatus: SliceStatus
  selectedPolicyName: string

  policyAssignee: {
    data: AssignedPolicyTypes[]
    snapshot: AssignedPolicyTypes[]
    status: SliceStatus
    delete_groups: string[]
    delete_users: string[]
  }

  cloudApp: {
    data: CloudAppResponseType
    snapshot: CloudAppResponseType
    status: SliceStatus
    resetCloudStatus?: SliceStatus
    baseCloudApp?: CloudAppResponseType
  }

  policyClashGroups: PolicyClashDataInterface
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  policyClashLastEvaluatedKey: any
  policyClashGroupsStatus: SliceStatus

  status: SliceStatus
  error: string
  updatedPolicyField?: PolicyUpdateField
}

const policiesSlice = createSlice({
  name: 'policies',
  initialState: {
    sslInspectionState: {
      data: SSLInspectionStatus.ENABLED,
      snapshot: SSLInspectionStatus.ENABLED,
      basePolicy: SSLInspectionStatus.ENABLED,
    },
    policies: {
      items: [],
      lastEvaluateKey: {} as PoliciesLastEvaluatedKeyTypes,
      hasMore: true,
      status: SliceStatus.idle,
      itemCount: 0 as number,
    },
    showCreatePolicyRP: false,
    createPolicyStatus: SliceStatus.idle,
    updatePolicyStatus: SliceStatus.idle,
    deletePolicyStatus: SliceStatus.idle,

    policyAssignee: {
      data: [] as AssignedPolicyTypes[],
      snapshot: [] as AssignedPolicyTypes[],
      status: SliceStatus.idle,
      delete_groups: [],
      delete_users: [],
      add_users: [],
      add_groups: [],
    },

    cloudApp: {
      data: {} as CloudAppResponseType,
      snapshot: {} as CloudAppResponseType,
      status: SliceStatus.idle,
      resetCloudStatus: SliceStatus.idle,
      baseCloudApp: {} as CloudAppResponseType,
    },

    status: SliceStatus.idle,
    error: '',
    selectedPolicyName: (localStorage.getItem('selectedPolicyName')
      ? localStorage.getItem('selectedPolicyName')
      : Policy.BasePolicy) as string,
    updatedPolicyField: {} as PolicyUpdateField,

    policyClashGroups: {
      participating_groups: [],
      grouped_clashes: [],
    } as PolicyClashDataInterface,
    policyClashLastEvaluatedKey: {} as UnknownObject,
    policyClashGroupsStatus: SliceStatus.idle,
    policyClashGroupsHasMoreRecord: false,
    policyReverted: false,
    formSubmitted: false,
    savePressed: false,
  },
  reducers: {},
  extraReducers: (builder: ActionReducerMapBuilder<PoliciesSliceState>) =>
    builder
      .addCase(updatePolicyName, (state, action) => ({
        ...state,
        selectedPolicyName: action.payload,
      }))
      .addCase(resetPolicyList, state => ({
        ...state,
        policies: {
          ...state.policies,
          items: [],
          lastEvaluateKey: {} as PoliciesLastEvaluatedKeyTypes,
          hasMore: true,
          status: SliceStatus.idle,
        },
      }))
      .addCase(createPolicy, state => ({
        ...state,
        createPolicyStatus: SliceStatus.pending,
      }))
      .addCase(createPolicyFailure, state => ({
        ...state,
        createPolicyStatus: SliceStatus.rejected,
      }))
      .addCase(createPolicySuccess, state => ({
        ...state,
        createPolicyStatus: SliceStatus.resolved,
      }))
      .addCase(setShowCreatePolicyRP, (state, action) => ({
        ...state,
        showCreatePolicyRP: action.payload,
      }))
      .addCase(deletePolicy, state => ({
        ...state,
        deletePolicyStatus: SliceStatus.pending,
      }))
      .addCase(deletePolicyFailure, state => ({
        ...state,
        deletePolicyStatus: SliceStatus.rejected,
      }))
      .addCase(deletePolicySuccess, state => ({
        ...state,
        deletePolicyStatus: SliceStatus.resolved,
      }))
      .addCase(getPolicyAssignee, state => ({
        ...state,
        policyAssignee: {...state.policyAssignee, status: SliceStatus.pending},
      }))
      .addCase(getPolicyAssigneeFailure, state => ({
        ...state,
        policyAssignee: {...state.policyAssignee, status: SliceStatus.rejected},
      }))
      .addCase(getPolicyAssigneeSuccess, (state, action) => ({
        ...state,
        policyAssignee: {
          ...state.policyAssignee,
          data: action.payload,
          snapshot: action.payload,
          status: SliceStatus.resolved,
        },
      }))
      .addCase(assignPolicy, state => ({
        ...state,
        updatePolicyStatus: SliceStatus.pending,
      }))
      .addCase(assignPolicyFailure, state => ({
        ...state,
        updatePolicyStatus: SliceStatus.rejected,
      }))
      .addCase(assignPolicySuccess, state => ({
        ...state,
        updatePolicyStatus: SliceStatus.resolved,
        policyAssignee: {
          ...state.policyAssignee,
          // reset delete groups and user on success
          delete_groups: [],
          delete_users: [],
          snapshot: state.policyAssignee.data,
        },
      }))
      .addCase(changeAssignedPolicy, (state, action) => ({
        ...state,
        policyAssignee: {
          ...state.policyAssignee,
          data: action.payload,
          ...determineAddedUsersOrGroups(
            current(state.policyAssignee.snapshot),
            action.payload,
          ),
        },
      }))
      .addCase(removeAssignedPolicySnapshot, state =>
        !isEqual(state.policyAssignee.data, state.policyAssignee.snapshot) ||
        state.policyAssignee.delete_groups.length > 0 ||
        state.policyAssignee.delete_users.length > 0
          ? {
              ...state,
              policyAssignee: {
                ...state.policyAssignee,
                data: state.policyAssignee.snapshot,
                delete_groups: [],
                delete_users: [],
                // in this case, original and updated state is same, so no need to determine added users or groups
                add_users: [],
                add_groups: [],
              },
            }
          : {
              ...state,
            },
      )
      .addCase(resetPolicyUserList, state => ({
        ...state,
        policyAssignee: {
          data: [] as AssignedPolicyTypes[],
          snapshot: [] as AssignedPolicyTypes[],
          status: SliceStatus.idle,
          delete_groups: [],
          delete_users: [],
          add_users: [],
          add_groups: [],
        },
      }))
      .addCase(setAssignedUsersGroupsToDelete, (state, action) => ({
        ...state,
        policyAssignee: {
          ...state.policyAssignee,
          [action.payload.type]: [
            ...state.policyAssignee[action.payload.type],
            action.payload.value,
          ],
        },
      }))
      .addCase(getCloudApp, state => ({
        ...state,
        cloudApp: {
          ...state.cloudApp,
          status: SliceStatus.pending,
        },
      }))
      .addCase(getCloudAppFailure, state => ({
        ...state,
        cloudApp: {
          ...state.cloudApp,
          status: SliceStatus.rejected,
        },
      }))
      .addCase(getCloudAppSuccess, (state, action) => ({
        ...state,
        cloudApp: {
          data: modifyCloudAppData(action.payload),
          snapshot: modifyCloudAppData(action.payload),
          status: SliceStatus.resolved,
        },
      }))
      .addCase(getBasePolicyCloudApp, state => ({
        ...state,
        cloudApp: {
          ...state.cloudApp,
          resetCloudStatus: SliceStatus.pending,
        },
      }))
      .addCase(getBasePolicyCloudAppFailure, state => ({
        ...state,
        cloudApp: {
          ...state.cloudApp,
          resetCloudStatus: SliceStatus.rejected,
        },
      }))
      .addCase(getBasePolicyCloudAppSuccess, (state, action) => ({
        ...state,
        cloudApp: {
          ...state.cloudApp,
          data: {
            inheritsCAC: true,
            cac: action.payload.cac,
          },
          baseCloudApp: action.payload,
          resetCloudStatus: SliceStatus.resolved,
        },
      }))
      .addCase(changeCloudApp, (state, action) => ({
        ...state,
        cloudApp: {
          ...state.cloudApp,
          data: action.payload,
          status: SliceStatus.resolved,
        },
      }))
      .addCase(updateCloudApp, state => ({
        ...state,
        cloudApp: {
          ...state.cloudApp,
          status: SliceStatus.pending,
        },
        status: SliceStatus.pending,
        savePressed: false,
        formSubmitted: false,
      }))
      .addCase(updateCloudAppFailure, state => ({
        ...state,
        cloudApp: {
          ...state.cloudApp,
          status: SliceStatus.rejected,
        },
        status: SliceStatus.rejected,
      }))
      .addCase(updateCloudAppSuccess, state => ({
        ...state,
        cloudApp: {
          ...state.cloudApp,
          snapshot: state.cloudApp.data,
          status: SliceStatus.resolved,
        },
        status: SliceStatus.resolved,
      }))
      .addCase(removeCloudAppSnapshot, state => ({
        ...state,
        cloudApp: {
          ...state.cloudApp,
          status: SliceStatus.resolved,
          data: state.cloudApp.snapshot,
        },
        policyReverted: true,
      }))
      .addCase(updatePolicyUpdateField, (state, action) => ({
        ...state,
        updatedPolicyField: {
          ...state.updatedPolicyField,
          ...action.payload,
        },
      }))
      .addCase(resetPolicyUpdateField, state => ({
        ...state,
        updatedPolicyField: {
          value1: '',
          value2: '',
          throwError: false,
          throwError1: false,
          throwError2: false,
        },
        policyReverted: false,
        savePressed: false,
      }))
      .addCase(formSubmitted, (state, action) => ({
        ...state,
        formSubmitted: action.payload,
      }))
      .addCase(savePressed, (state, action) => ({
        ...state,
        savePressed: action.payload,
      }))
      .addCase(getPolicyClashGroups, state => ({
        ...state,
        policyClashGroupsStatus: SliceStatus.pending,
      }))
      .addCase(getPolicyClashGroupsFailure, state => ({
        ...state,
        policyClashGroupsStatus: SliceStatus.rejected,
      }))
      .addCase(getPolicyClashGroupsSuccess, (state, action) => ({
        ...state,
        policyClashGroups: {
          ...state?.policyClashGroups,
          ...{
            participating_groups: [
              ...state?.policyClashGroups?.participating_groups,
              ...(action?.payload?.data?.participating_groups || []),
            ],
            grouped_clashes: [
              ...state?.policyClashGroups?.grouped_clashes,
              ...(action?.payload?.data?.grouped_clashes || []),
            ],
          },
        },
        policyClashLastEvaluatedKey: action.payload.LastEvaluatedKey,
        policyClashGroupsStatus: SliceStatus.resolved,
        policyClashGroupsHasMoreRecord:
          action.payload &&
          'LastEvaluatedKey' in action.payload &&
          action.payload.LastEvaluatedKey &&
          !isEmptyObject(action.payload.LastEvaluatedKey),
      }))

      .addCase(resetPolicyClash, state => ({
        ...state,
        policyClashGroups: {
          participating_groups: [],
          grouped_clashes: [],
        } as PolicyClashDataInterface,
        policyClashLastEvaluatedKey: {} as UnknownObject,
        policyClashGroupsHasMoreRecord: false,
      }))
      // Set SSL Inspection state in data
      .addCase(setSSLInspectionData, (state, action) => ({
        ...state,
        sslInspectionState: {
          ...state.sslInspectionState,
          data: action.payload,
        },
      }))
      .addCase(setSSLInspectionSnapshot, (state, action) => ({
        ...state,
        sslInspectionState: {
          ...state.sslInspectionState,
          snapshot: action.payload,
        },
      }))
      .addCase(setSSLInspectionBasePolicy, (state, action) => ({
        ...state,
        sslInspectionState: {
          ...state.sslInspectionState,
          basePolicy: action.payload,
        },
      }))
      .addDefaultCase(state => state),
})

// Only for cloudapp (salesforce and box)
// add 'defaultcloudapplication' as blockpage if api response for cac.blockpage is empty
const modifyCloudAppData = (
  data: CloudAppResponseType,
): CloudAppResponseType => {
  let updatedData = {...data}
  if (data?.cac) {
    let updatedCac = {...data?.cac}
    Object.entries(updatedCac).forEach(([key, value]) => {
      if (key === salesforce || key === box) {
        updatedCac = {
          ...updatedCac,
          [key]: {
            ...value,
            blockPage: value?.blockPage || defaultCloudApplication,
          },
        }
      }
    })

    updatedData = {...updatedData, cac: updatedCac}
  }
  return {...updatedData}
}

export const {reducer: policiesReducer, name: policiesReducerName} =
  policiesSlice

export type TPoliciesActions =
  | ReturnType<typeof updatePolicyName>
  | ReturnType<typeof createPolicy>
  | ReturnType<typeof createPolicySuccess>
  | ReturnType<typeof createPolicyFailure>
  | ReturnType<typeof setShowCreatePolicyRP>
  | ReturnType<typeof deletePolicy>
  | ReturnType<typeof getPolicyAssignee>
  | ReturnType<typeof getPolicyAssigneeFailure>
  | ReturnType<typeof getPolicyAssigneeSuccess>
  | ReturnType<typeof assignPolicy>
  | ReturnType<typeof assignPolicyFailure>
  | ReturnType<typeof assignPolicySuccess>
  | ReturnType<typeof changeAssignedPolicy>
  | ReturnType<typeof removeAssignedPolicySnapshot>
  | ReturnType<typeof setAssignedUsersGroupsToDelete>
  | ReturnType<typeof getCloudApp>
  | ReturnType<typeof getCloudAppSuccess>
  | ReturnType<typeof getCloudAppFailure>
  | ReturnType<typeof changeCloudApp>
  | ReturnType<typeof removeCloudAppSnapshot>
  | ReturnType<typeof resetPolicyList>
  | ReturnType<typeof updateCloudApp>
  | ReturnType<typeof updateCloudAppSuccess>
  | ReturnType<typeof getBasePolicyCloudApp>
  | ReturnType<typeof getBasePolicyCloudAppSuccess>
  | ReturnType<typeof getBasePolicyCloudAppFailure>
  | ReturnType<typeof updatePolicyUpdateField>
  | ReturnType<typeof resetPolicyUpdateField>
  | ReturnType<typeof formSubmitted>
  | ReturnType<typeof savePressed>
  | ReturnType<typeof getPolicyClashGroups>
  | ReturnType<typeof getPolicyClashGroupsFailure>
  | ReturnType<typeof getPolicyClashGroupsSuccess>
  | ReturnType<typeof resetPolicyClash>
  | ReturnType<typeof resetPolicyUserList>

export const policiesActions = {
  createPolicy,
  createPolicySuccess,
  createPolicyFailure,
  setShowCreatePolicyRP,
  deletePolicy,
  deletePolicyFailure,
  deletePolicySuccess,
  getPolicyAssignee,
  getPolicyAssigneeFailure,
  getPolicyAssigneeSuccess,
  assignPolicy,
  assignPolicyFailure,
  assignPolicySuccess,
  changeAssignedPolicy,
  removeAssignedPolicySnapshot,
  setAssignedUsersGroupsToDelete,
  getCloudApp,
  getCloudAppSuccess,
  getCloudAppFailure,
  changeCloudApp,
  removeCloudAppSnapshot,
  resetPolicyList,
  updateCloudApp,
  updateCloudAppSuccess,
  updateCloudAppFailure,
  getBasePolicyCloudApp,
  getBasePolicyCloudAppSuccess,
  getBasePolicyCloudAppFailure,
  updatePolicyName,
  updatePolicyUpdateField,
  resetPolicyUpdateField,
  formSubmitted,
  savePressed,
  getPolicyClashGroups,
  getPolicyClashGroupsFailure,
  getPolicyClashGroupsSuccess,
  resetPolicyClash,
  resetPolicyUserList,
}

export type PoliciesState = ReturnType<typeof policiesReducer>

interface AddUsersOrGroups {
  add_users: string[]
  add_groups: string[]
}
/**
 * Determines the users or groups that have been added by comparing the original state with the updated state.
 *
 * @param originalState - The original list of assigned policy users and group.
 * @param updatedState - The updated list of assigned policy users and group.
 * @returns An object containing the users and groups that have been added.
 */
const determineAddedUsersOrGroups = (
  originalState: AssignedPolicyTypes[],
  updatedState: AssignedPolicyTypes[],
): AddUsersOrGroups => {
  const users = updatedState.filter(
    d =>
      d.type === 'users' && !originalState.some(o => o.emailId === d.emailId),
  )
  const groups = updatedState.filter(
    d =>
      d.type === 'groups' && !originalState.some(o => o.emailId === d.emailId),
  )
  return {
    add_users: users.map(u => u.emailId),
    add_groups: groups.map(g => g.emailId),
  }
}
